import type { VariantProps } from "tailwind-variants";

import { dataFocusVisibleClasses } from "../utils";
import { cardShadows } from "../utils/shadows";
import { tv } from "../utils/tv";

/**
 * Card **Tailwind Variants** component
 *
 * @example
 * ```js
 * const {base, header, body, footer} = card({...})
 *
 * <div className={base()}>
 *    <div className={header()}>Header</div>
 *    <div className={body()}>Body</div>
 *    <div className={footer()}>Footer</div>
 * </div>
 * ```
 */
const card = tv({
  slots: {
    base: [
      "flex",
      "flex-col",
      "relative",
      "overflow-hidden",
      "height-auto",
      "outline-none",
      "text-foreground",
      "box-border",
      "m-0",
      "p-0",
      // focus ring
      ...dataFocusVisibleClasses,
    ],
    header: [
      "flex",
      "gap-3",
      "z-10",
      "w-full",
      "justify-start",
      "items-center",
      "shrink-0",
      "overflow-inherit",
      "color-inherit",
      "subpixel-antialiased",
    ],
    body: [
      "relative",
      "flex",
      "flex-1",
      "w-full",
      "flex-auto",
      "flex-col",
      "place-content-inherit",
      "align-items-inherit",
      "h-auto",
      "break-words",
      "text-left",
      "overflow-y-auto",
      "subpixel-antialiased",
    ],
    footer: [
      "h-auto",
      "flex",
      "w-full",
      "items-center",
      "overflow-hidden",
      "color-inherit",
      "subpixel-antialiased",
    ],
    startContent: [],
    endContent: ["ml-auto"],
    textureContainer: ["hidden"],
    texture: ["hidden"],
  },
  variants: {
    border: {
      true: {
        base: "border border-neutral-200",
      },
      false: {
        base: "border-none",
      },
    },
    disableShadows: {
      true: {},
      false: {},
    },
    size: {
      sm: {
        header: "p-2",
        footer: "p-2",
        body: "p-2",
      },
      md: {
        header: "p-4",
        footer: "p-4",
        body: "p-4",
      },
      lg: {
        header: "p-6",
        footer: "p-6",
        body: "p-6",
      },
    },
    divider: {
      true: {
        header: "border-b border-neutral-200",
        footer: "border-t border-neutral-200",
      },
      false: {},
    },
    radius: {
      none: {
        base: "rounded-none",
        header: "rounded-none",
        footer: "rounded-none",
      },
      Xsm: {
        base: "rounded",
        header: "rounded-t",
        footer: "rounded-b",
      },
      sm: {
        base: "rounded-small",
        header: "rounded-t-small",
        footer: "rounded-b-small",
      },
      md: {
        base: "rounded-medium",
        header: "rounded-t-medium",
        footer: "rounded-b-medium",
      },
      lg: {
        base: "rounded-large",
        header: "rounded-t-large",
        footer: "rounded-b-large",
      },
      Xlg: {
        base: "rounded-xl",
        header: "rounded-t-rounded-xl",
        footer: "rounded-b-rounded-xl",
      },
    },
    fullWidth: {
      true: {
        base: "w-full",
      },
    },
    isHoverable: {
      true: {
        base: "data-[hover=true]:bg-neutral-100 dark:data-[hover=true]:bg-neutral-100",
      },
    },
    isPressable: {
      true: { base: "cursor-pointer" },
    },
    isBlurred: {
      true: {
        base: [
          "bg-background/80",
          "dark:bg-background/20",
          "backdrop-blur-md",
          "backdrop-saturate-150",
        ],
      },
    },
    isFooterBlurred: {
      true: {
        footer: ["bg-background/10", "backdrop-blur", "backdrop-saturate-150"],
      },
    },
    isDisabled: {
      true: {
        base: "opacity-disabled cursor-not-allowed",
      },
    },
    texture: {
      lines: {
        textureContainer: [
          "block absolute top-[1px] right-[1px] w-[99%] h-[99%] overflow-hidden border-transparent",
        ],
        texture: ["block absolute text-neutral-100 left-[32%] top-[-76%]"],
      },
      smoke: {
        textureContainer: ["block absolute top-0 left-0"],
        texture: ["block"],
      },
    },
    isSelected: {
      true: {
        base: "bg-neutral-50 border-2 border-primary-500",
      },
    },
    disableAnimation: {
      true: "",
      false: { base: "transition-transform-background motion-reduce:transition-none" },
    },
    variant: {
      none: {},
      extension: {
        base: ["bg-transparent", "w-full md:min-w-80"],
        header: ["px-5 pt-5", "font-semibold"],
        body: ["p-5 pt-6", "text-sm", "text-foreground-500"],
        footer: [
          "px-5 pt-5",
          "border-t border-neutral-200",
          "justify-end",
          "font-semibold",
          "text-sm",
          "text-foreground-500",
        ],
        startContent: [
          "grid place-items-center",
          "w-[52px] h-[52px]",
          "rounded-lg",
          "border border-neutral-200",
        ],
        endContent: [
          "w-8 h-8 p-2",
          "grid place-items-center",
          "bg-foreground-200",
          "text-foreground",
          "text-medium",
          "rounded-full",
          "data-[connected=true]:bg-success-600/10",
          "data-[connected=true]:text-success-600",
        ],
      },
    },
  },

  defaultVariants: {
    border: true,
    radius: "Xlg",
    disableShadows: false,
    fullWidth: false,
    isHoverable: false,
    isPressable: false,
    isDisabled: false,
    disableAnimation: false,
    isFooterBlurred: false,
    isSelected: false,
    variant: "none",
    size: "md",
  },

  compoundVariants: [
    {
      isPressable: true,
      disableAnimation: false,
      class: "data-[pressed=true]:scale-[0.97] tap-highlight-transparent",
    },
    {
      isSelected: false,
      disableShadows: false,
      class: {
        base: ["border-none", ...cardShadows],
        startContent: ["border-none", ...cardShadows],
      },
    },
  ],
});

export type CardVariantProps = VariantProps<typeof card>;
export type CardSlots = keyof ReturnType<typeof card>;
export type CardReturnType = ReturnType<typeof card>;

export { card };
